import React from 'react';
import {useTranslation} from 'react-i18next';
import {type Course} from '../../../api/courcesService';
import './CourseCards.css';
import {FALLBACK_IMG} from "../../../constants/constants";

export interface CourseCardsProps {
  items: Course[];
  onCourseClick: (course: Course) => void;
  viewProgress?: boolean;
  loading?: boolean;
}

const SkeletonCard: React.FC = () => (
  <div className="course-card skeleton" aria-hidden="true">
    <div className="course-image">
      <div className="skeleton-box skeleton-image" />
    </div>
    <div className="course-info">
      <div className="skeleton-box skeleton-pill" />
      <div className="skeleton-box skeleton-line long" />
      <div className="skeleton-box skeleton-line" />
    </div>
  </div>
);

const CourseCards: React.FC<CourseCardsProps> = ({items, onCourseClick, viewProgress = false, loading = false}) => {
  const {t} = useTranslation();

  // If not loading and no items — render nothing (keeps previous behavior)
  if (!loading && (!items || items.length === 0)) return null;

  if (loading) {
    const skeletons = Array.from({ length: 4 });
    return (
      <div className="courses" aria-busy="true" aria-live="polite">
        {skeletons.map((_, idx) => (
          <SkeletonCard key={idx} />
        ))}
      </div>
    );
  }

  return (
    <div className="courses">
      {items.map((course) => {
        const total = course.totalLessons || 0;
        const completed = course.lessonsCompleted || 0;
        const width = total > 0 ? (completed / total) * 100 : 0;
        const imgSrc = course.featuredImage?.url || FALLBACK_IMG;

        return (
          <div
            key={course.id}
            className="course-card"
            onClick={() => onCourseClick(course)}
            style={{cursor: 'pointer'}}
          >
            <div className="course-image">
              <img
                src={imgSrc}
                alt={course.title}
                onError={(e) => {
                  console.error(e);
                  const target = e.target as HTMLImageElement;
                  target.src = FALLBACK_IMG;
                }}
              />
            </div>
            <div className="course-info">
              <div className="course-level">{course.difficultyLevel}</div>
              <h3 className="course-title">{course.title}</h3>
              <div className="course-progress">
                {t('product.courses.lessons')} {completed}/{total}
              </div>
              {viewProgress && (
                <>
                  <div className="course-progress-bar">
                    <div className="course-progress-fill" style={{width: `${width}%`}}/>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CourseCards;
